import React, { useState, useEffect } from "react";
import {
  FaTruck,
  FaPhoneAlt,
  FaCogs,
  FaMicrophone,
  FaHeart,
  FaMapMarkerAlt,
  FaClock,
  FaCalendarAlt,
  FaShoppingCart,
  FaWarehouse,
  FaShoppingBag,
  FaStore,
  FaPalette,
  FaBarcode,
  FaSitemap
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { axiosFunction } from "../../components/commonFunctions";
import { v2ReactURL } from "../../utils/constant";
import { toast } from "react-toastify";
import ColorPicker from "./ColorPicker";
import { Wrapper, WrapperCard, WrapperContent, WrapperSubHeader } from "../../components/common";
import { Row } from "reactstrap";

// ---------------- Card Component ----------------
const Card = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      background: "#fff",
      borderRadius: 8,
      padding: "24px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      marginBottom: "20px",
    }}
  >
    {children}
  </div>
);

// ---------------- Switch Component ----------------
const Switch = ({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) => (
  <div style={{ position: "relative" }}>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      style={{
        position: "absolute",
        opacity: 0,
        cursor: "pointer",
        height: 0,
        width: 0,
      }}
    />
    <div
      style={{
        width: "44px",
        height: "24px",
        background: checked ? "#4f46e5" : "#e5e7eb",
        borderRadius: "12px",
        position: "relative",
        cursor: "pointer",
        transition: "background-color 0.2s",
      }}
      onClick={() => onChange(!checked)}
    >
      <div
        style={{
          position: "absolute",
          top: "2px",
          left: checked ? "22px" : "2px",
          width: "20px",
          height: "20px",
          background: "#fff",
          borderRadius: "50%",
          transition: "left 0.2s",
          boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
        }}
      />
    </div>
  </div>
);

// ---------------- Radio Button Component ----------------
const RadioButton = ({ 
  label, 
  checked, 
  onChange, 
  value,
  description 
}: { 
  label: string; 
  checked: boolean; 
  onChange: (v: string) => void; 
  value: string;
  description?: string;
}) => (
  <div 
    style={{ 
      display: "flex", 
      alignItems: "flex-start", 
      gap: "8px",
      cursor: "pointer",
      padding: "12px",
      border: checked ? "1px solid #4f46e5" : "1px solid #e5e7eb",
      borderRadius: "6px",
      background: checked ? "#f8faff" : "#fff",
      transition: "all 0.2s",
    }}
    onClick={() => onChange(value)}
  >
    <div style={{ position: "relative", marginTop: "2px" }}>
      <input
        type="radio"
        checked={checked}
        onChange={() => onChange(value)}
        style={{
          position: "absolute",
          opacity: 0,
          cursor: "pointer",
          height: 0,
          width: 0,
        }}
      />
      <div
        style={{
          width: "16px",
          height: "16px",
          border: checked ? "4px solid #4f46e5" : "2px solid #d1d5db",
          borderRadius: "50%",
          background: checked ? "#fff" : "#fff",
          transition: "all 0.2s",
        }}
      />
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: "14px", fontWeight: "500", color: "#1f2937" }}>{label}</div>
      {description && (
        <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>{description}</div>
      )}
    </div>
  </div>
);

// ---------------- Feature Box ----------------
const FeatureBox = ({
  title,
  description,
  checked,
  onChange,
  icon,
  show = true,
}: {
  title: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  icon?: React.ReactNode;
  show?: boolean;
}) => {
  if (!show) return null;

  return (
    <div
      style={{
        padding: "16px",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", fontWeight: 500 }}>
        {icon}
        {title}
      </div>
      <div style={{ fontSize: "12px", color: "#6b7280" }}>{description}</div>
      <div style={{ marginLeft: "auto" }}>
        <Switch checked={checked} onChange={onChange} />
      </div>
    </div>
  );
};

// ---------------- Input Field Component ----------------
const InputField = ({
  label,
  placeholder,
  value,
  onChange,
  type = "text",
  unit,
  show = true,
}: {
  label: string;
  placeholder: string;
  value: any;
  onChange: (v: any) => void;
  type?: string;
  unit?: string;
  show?: boolean;
}) => {
  if (!show) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (type === "number") {
      // Handle empty input for numbers
      const inputValue = e.target.value;
      if (inputValue === "") {
        onChange(0);
      } else {
        const numValue = Number(inputValue);
        if (!isNaN(numValue)) {
          onChange(numValue);
        }
      }
    } else {
      onChange(e.target.value);
    }
  };

 const displayValue = value;

  return (
    <div>
      <div style={{ fontSize: "14px", marginBottom: "8px", fontWeight: "500" }}>{label}</div>
      <div style={{ position: "relative" }}>
        <input
          type={type}
          value={displayValue}
          onChange={handleChange}
          placeholder={placeholder}
          style={{
            width: "100%",
            padding: "8px 12px",
            border: "1px solid #e5e7eb",
            borderRadius: "6px",
            background: "#fff",
            color: "#1c1c1cff",
          }}
        />
        {unit && (
          <div style={{
            position: "absolute",
            right: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            color: "#6b7280",
            fontSize: "12px",
          }}>
            {unit}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to convert boolean to API string value
const booleanToString = (value: boolean): string => {
  return value ? "1" : "0";
};

// ---------------- Main Component ----------------
const NativeSetting = ({ navUrl }: { navUrl: string }) => {
  // Redux data
  const sessionList = useSelector((state: any) => state.sessionData.sessionScope);

  // Driver Management States
  const [driverApp, setDriverApp] = useState(false);
  const [driverIncentive, setDriverIncentive] = useState(false);
  const [orderCommission, setOrderCommission] = useState(0);
  const [orderCommissionType, setOrderCommissionType] = useState("amount"); // "amount" or "percentage"
  const [kmCharge, setKmCharge] = useState(0);
  const [commissionOnFulfillment, setCommissionOnFulfillment] = useState(false);

  // Contact Information States
  const [flatBlock, setFlatBlock] = useState(false);
  const [guardianName, setGuardianName] = useState(false);

  // Application Features States
  const [voiceFeature, setVoiceFeature] = useState(false);
  const [wishlist, setWishlist] = useState(false);
  const [locationTracking, setLocationTracking] = useState(false);
  const [isProductScanning, setIsProductScanning] = useState(false);
  const [displayDepartment, setDisplayDepartment] = useState(false);

  // Operation Management States
  const [deliveryScheduling, setDeliveryScheduling] = useState(false);
  const [deliverySlot, setDeliverySlot] = useState(false);
  const [deliverySlotType, setDeliverySlotType] = useState("General");
  const [scheduledDelivery, setScheduledDelivery] = useState(false);
  const [orderCancellationWindow, setOrderCancellationWindow] = useState(24);
  const [cutOffTime, setCutOffTime] = useState("");
  const [estimatedDeliveryTime, setEstimatedDeliveryTime] = useState(30);
  const [approvalRequired, setApprovalRequired] = useState(false);
  const [deliverySheetLimit, setDeliverySheetLimit] = useState(0);
  const [deliveryZoneType, setDeliveryZoneType] = useState("radius");
  const [deliveryRadius, setDeliveryRadius] = useState(10);
  const [deliveryZoneValue, setDeliveryZoneValue] = useState("");
  const [hoSupplierCommission, setHoSupplierCommission] = useState(false);
  const [deliveryCutoffTime, setDeliveryCutoffTime] = useState("");
  const [approvalRequiredForOrder, setApprovalRequiredForOrder] = useState(false);
  const [primaryColor, setPrimaryColor] = useState("#8090FF");
  const [secondaryColor, setSecondaryColor] = useState("#108981");
  const [tertiaryColor, setTertiaryColor] = useState("#F59E8B");

  // Purchase Options States
  const [purchaseOptions, setPurchaseOptions] = useState(false);

  // Branch Operations States
  const [branchStockAdjustment, setBranchStockAdjustment] = useState(false);

  // Define a type for settings map
  type SettingValue = {
    value?: string;
    addValue?: string;
    id: number;
  };
  
  // Store all settings for easy access
  const [settingsMap, setSettingsMap] = useState<Record<string, SettingValue>>({});
  const [isLoading, setIsLoading] = useState(false);

  // Helper function to convert numeric values to booleans
  const numericToBoolean = (value: string | number | null | undefined): boolean => {
    if (value === null || value === undefined) return false;
    return Number(value) === 1;
  };

  // Helper function to check if a setting exists
  const hasSetting = (settingKey: string) => {
    return !!settingsMap[settingKey] && (settingsMap[settingKey].id ?? 0) !== 0;
  };

  // Helper function to check if any setting exists in a section
  const hasAnySetting = (settingKeys: string[]) => {
    return settingKeys.some(key => hasSetting(key));
  };

  // ---------------- API Call Functions ----------------
  
  // API for switches (boolean values)
  const saveSwitchSetting = async (type: string, value: boolean, currentId?: number) => {
    try {
      setIsLoading(true);

      const payload = {
        type: type,
        id: currentId || 0,
        value: booleanToString(value),
        savetype: 1,
        normal: 0
      };

      const res = await axiosFunction(
        `${v2ReactURL}/nativeSetting/companysettingsavedata`,
        "POST",
        payload,
        false
      );

      if (res.data.response) {
        // Update local settings map
        setSettingsMap(prev => ({
          ...prev,
          [type]: {
            ...prev[type],
            value: booleanToString(value),
            id: res.data.response.companySettingId || currentId || prev[type]?.id
          }
        }));
        return true;
      } else {
        toast.error(`Failed to update setting`);
        return false;
      }
    } catch (error) {
      console.error(`Error saving switch setting ${type}:`, error);
      toast.error(`Error updating setting`);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // API for inputs (text/number values)
  const saveInputSetting = async (type: string, value: any, currentId?: number) => {
    try {
      setIsLoading(true);

      const payload = {
        type: type,
        id: currentId || 0,
        value: value.toString(),
        savetype: 2,
        normal: 0
      };

      const res = await axiosFunction(
        `${v2ReactURL}/nativeSetting/companysettingsaveaddvalue`,
        "POST",
        payload,
        false
      );

      if (res.data.response) {
        // Update local settings map
        setSettingsMap(prev => ({
          ...prev,
          [type]: {
            ...prev[type],
            value: value.toString(),
            id: res.data.response.companySettingId || currentId || prev[type]?.id
          }
        }));
        return true;
      } else {
        toast.error(`Failed to update setting`);
        return false;
      }
    } catch (error) {
      console.error(`Error saving input setting ${type}:`, error);
      toast.error(`Error updating setting`);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------- Handler Functions ----------------
  
  // Handler for boolean settings (switches)
  const handleBooleanSettingChange = async (type: string, value: boolean, currentId?: number) => {
    return await saveSwitchSetting(type, value, currentId);
  };

  // Handler for numeric settings (inputs)
  const handleNumericSettingChange = async (type: string, value: number, currentId?: number) => {
    return await saveInputSetting(type, value, currentId);
  };

  // Handler for text settings (inputs)
  const handleTextSettingChange = async (type: string, value: string, currentId?: number) => {
    return await saveInputSetting(type, value, currentId);
  };

  // Handler for select settings (dropdowns)
  const handleSelectSettingChange = async (type: string, value: string, currentId?: number) => {
    return await saveInputSetting(type, value, currentId);
  };

  // ---------------- Wrapped State Setters ----------------
  
  // Driver Management
  const handleDriverAppChange = async (value: boolean) => {
    const success = await handleBooleanSettingChange(
      'allowDriverApp',
      value,
      settingsMap.allowDriverApp?.id
    );

    if (success) {
      setDriverApp(value);

      // If driver app turned OFF (value = false)
      if (!value) {
        // Reset related values
        await handleBooleanSettingChange('allowDriverIncentive', false, settingsMap.allowDriverIncentive?.id);
        await handleTextSettingChange('perOrderCommission', '0', settingsMap.perOrderCommission?.id);
        await handleTextSettingChange('perKmCharge', '0', settingsMap.perKmCharge?.id);

        // Update local states as well
        setDriverIncentive(false);
        setOrderCommission(0);
        setKmCharge(0);
      }
    }
  };

  const handleDriverIncentiveChange = async (value: boolean) => {
    const success = await handleBooleanSettingChange('allowDriverIncentive', value, settingsMap.allowDriverIncentive?.id);
    if (success) {
      setDriverIncentive(value);
      if (value) {
        // Show toast when Driver Incentive is enabled
        if (hasSetting('perOrderCommission') && hasSetting('perKmCharge')) {
          toast.error("Per Order Commission and Per KM Charge (₹) are mandatory. Please set values or default amounts will be used.");
        } else if (hasSetting('perOrderCommission')) {
          toast.error("Per Order Commission is mandatory. Please set a value or default amount will be used.");
        } else if (hasSetting('perKmCharge')) {
          toast.error("Per KM Charge (₹) is mandatory. Please set a value or default amount will be used.");
        }

        // Set default values if current values are empty/zero
        if (hasSetting('perOrderCommission') && (!orderCommission || orderCommission === 0)) {
          handleOrderCommissionChange(0);
        }
        if (hasSetting('perKmCharge') && (!kmCharge || kmCharge === 0)) {
          handleKmChargeChange(0);
        }
      }
    }
  };

  const handleOrderCommissionChange = async (value: number) => {
    const success = await handleNumericSettingChange('perOrderCommission', value, settingsMap.perOrderCommission?.id);
    if (success) setOrderCommission(value);
  };

  const handleOrderCommissionTypeChange = async (value: string) => {
    const success = await handleSelectSettingChange('perOrderCommissionType', value, settingsMap.perOrderCommissionType?.id);
    if (success) setOrderCommissionType(value);
  };

  const handleKmChargeChange = async (value: number) => {
    const success = await handleNumericSettingChange('perKmCharge', value, settingsMap.perKmCharge?.id);
    if (success) setKmCharge(value);
  };

  const handleCommissionOnFulfillmentChange = async (value: boolean) => {
    const success = await handleBooleanSettingChange('commissionOnFulfillment', value, settingsMap.commissionOnFulfillment?.id);
    if (success) setCommissionOnFulfillment(value);
  };

  // Contact Information
  const handleFlatBlockChange = async (value: boolean) => {
    const success = await handleBooleanSettingChange('allowFlatBlockField', value, settingsMap.allowFlatBlockField?.id);
    if (success) setFlatBlock(value);
  };

  const handleGuardianNameChange = async (value: boolean) => {
    const success = await handleBooleanSettingChange('allowGuardianName', value, settingsMap.allowGuardianName?.id);
    if (success) setGuardianName(value);
  };

  // Application Features
  const handleVoiceFeatureChange = async (value: boolean) => {
    const success = await handleBooleanSettingChange('allowVoiceFeature', value, settingsMap.allowVoiceFeature?.id);
    if (success) setVoiceFeature(value);
  };

  const handleWishlistChange = async (value: boolean) => {
    const success = await handleBooleanSettingChange('allowWishlist', value, settingsMap.allowWishlist?.id);
    if (success) setWishlist(value);
  };

  const handleLocationTrackingChange = async (value: boolean) => {
    const success = await handleBooleanSettingChange('allowDriverTracking', value, settingsMap.allowDriverTracking?.id);
    if (success) setLocationTracking(value);
  };

  const handleProductScanningChange = async (value: boolean) => {
    const success = await handleBooleanSettingChange('isProductScanning', value, settingsMap.isProductScanning?.id);
    if (success) setIsProductScanning(value);
  };

  const handleDisplayDepartmentChange = async (value: boolean) => {
    const success = await handleBooleanSettingChange(
      'displayDepartmentInCustomerApp', 
      value, 
      settingsMap.displayDepartmentInCustomerApp?.id
    );
    if (success) setDisplayDepartment(value);
  };

  // Operation Management
  const handleDeliverySchedulingChange = async (value: boolean) => {
    const success = await handleBooleanSettingChange('allowDeliveryScheduling', value, settingsMap.allowDeliveryScheduling?.id);
    if (success) setDeliveryScheduling(value);
  };

  const handleDeliverySlotChange = async (value: boolean) => {
    const success = await handleBooleanSettingChange('allowDeliverySlot', value, settingsMap.allowDeliverySlot?.id);
    if (success) setDeliverySlot(value);
  };

  const handleDeliverySlotTypeChange = async (value: string) => {
    const success = await handleSelectSettingChange('deliverySlotType', value, settingsMap.deliverySlotType?.id);
    if (success) setDeliverySlotType(value);
  };

  const handleScheduledDeliveryChange = async (value: boolean) => {
    const success = await handleBooleanSettingChange('allowScheduledDelivery', value, settingsMap.allowScheduledDelivery?.id);
    if (success) setScheduledDelivery(value);
  };

  const handleOrderCancellationWindowChange = async (value: number) => {
    const success = await handleNumericSettingChange('orderCancellationWindow', value, settingsMap.orderCancellationWindow?.id);
    if (success) setOrderCancellationWindow(value);
  };

  const handleCutOffTimeChange = async (value: string) => {
    const success = await handleTextSettingChange('cutOffTime', value, settingsMap.cutOffTime?.id);
    if (success) setCutOffTime(value);
  };

  const handleEstimatedDeliveryTimeChange = async (value: number) => {
    const success = await handleNumericSettingChange('estimatedDeliveryTime', value, settingsMap.estimatedDeliveryTime?.id);
    if (success) setEstimatedDeliveryTime(value);
  };

  const handleApprovalRequiredChange = async (value: boolean) => {
    const success = await handleBooleanSettingChange('approvalRequired', value, settingsMap.approvalRequired?.id);
    if (success) setApprovalRequired(value);
  };

  const handleDeliverySheetLimitChange = async (value: number) => {
    const success = await handleNumericSettingChange('deliverySheetLimit', value, settingsMap.deliverySheetLimit?.id);
    if (success) setDeliverySheetLimit(value);
  };

  // Delivery Zone Handlers
  const handleDeliveryZoneTypeChange = async (value: string) => {
    const success = await handleSelectSettingChange('deliveryZoneType', value, settingsMap.deliveryZoneType?.id);
    if (success) {
      setDeliveryZoneType(value);
      // Reset delivery zone value when type changes
      if (value === "radius") {
        await handleDeliveryZoneValueChange("10");
        setDeliveryRadius(10);
      } else if (value === "pincode") {
        await handleDeliveryZoneValueChange("");
        setDeliveryZoneValue("");
      }
    }
  };

  const handleDeliveryZoneValueChange = async (value: string) => {
    const success = await handleTextSettingChange('deliveryZoneValue', value, settingsMap.deliveryZoneValue?.id);
    if (success) {
      setDeliveryZoneValue(value);
      if (deliveryZoneType === "radius") {
        setDeliveryRadius(Number(value) || 0);
      }
    }
  };

  const handleDeliveryRadiusChange = async (value: number) => {
    await handleDeliveryZoneValueChange(value.toString());
  };

  const handlePincodesChange = async (value: string) => {
    await handleDeliveryZoneValueChange(value);
  };

  const handleHoSupplierCommissionChange = async (value: boolean) => {
    const success = await handleBooleanSettingChange('hoSupplierCommission', value, settingsMap.hoSupplierCommission?.id);
    if (success) setHoSupplierCommission(value);
  };

  const handleDeliveryCutoffTimeChange = async (value: string) => {
    const success = await handleTextSettingChange('deliveryCutoffTime', value, settingsMap.deliveryCutoffTime?.id);
    if (success) setDeliveryCutoffTime(value);
  };

  const handleApprovalRequiredForOrderChange = async (value: boolean) => {
    const success = await handleBooleanSettingChange('approvalRequiredForOrder', value, settingsMap.approvalRequiredForOrder?.id);
    if (success) setApprovalRequiredForOrder(value);
  };

  // Purchase Options
  const handlePurchaseOptionsChange = async (value: boolean) => {
    const success = await handleBooleanSettingChange('allowPurchaseOption', value, settingsMap.allowPurchaseOption?.id);
    if (success) setPurchaseOptions(value);
  };

  // Branch Operations
  const handleBranchStockAdjustmentChange = async (value: boolean) => {
    const success = await handleBooleanSettingChange('allowBranchStockAdjustment', value, settingsMap.allowBranchStockAdjustment?.id);
    if (success) setBranchStockAdjustment(value);
  };

  // Color Handlers
  const handleColorChange = async (color: string, type: string): Promise<boolean> => {
    try {
      const success = await handleTextSettingChange(type, color, settingsMap[type]?.id);
      
      if (success) {
        // Update local state based on color type
        switch (type) {
          case 'primaryColourStatus':
            setPrimaryColor(color);
            break;
          case 'secondaryColourStatus':
            setSecondaryColor(color);
            break;
          case 'tertiaryColourStatus':
            setTertiaryColor(color);
            break;
          default:
            break;
        }
        return true;
      } else {
        toast.error(`Failed to update ${type.replace('ColourStatus', '')} color`);
        return false;
      }
    } catch (error) {
      console.error(`Error saving ${type}:`, error);
      toast.error(`Error updating color`);
      return false;
    }
  };

  // ---------------- Fetch Data from API ----------------
  useEffect(() => {
    const fetchNativeSettingList = async () => {
      try {
        const res = await axiosFunction(
          `${v2ReactURL}/nativeSetting/nativeSettingList`,
          "GET",
          null,
          false
        );

        console.log("Native Setting List:", res);

        if (res.data.response) {
          // Create a map for easier lookup
          const settings = res.data.response.reduce((acc: any, setting: any) => {
            acc[setting.type] = {
              value: setting.value,
              addValue: setting.addValue,
              id: setting.companySettingId
            };
            return acc;
          }, {});

          setSettingsMap(settings);

          // Set Driver Management settings
          setDriverApp(numericToBoolean(settings.allowDriverApp?.value));
          setDriverIncentive(numericToBoolean(settings.allowDriverIncentive?.value));
          setOrderCommission(Number(settings.perOrderCommission?.addValue) || 0);
          setOrderCommissionType(settings.perOrderCommissionType?.addValue || "amount");
          setKmCharge(Number(settings.perKmCharge?.addValue) || 0);
          setCommissionOnFulfillment(numericToBoolean(settings.commissionOnFulfillment?.value));

          // Set Contact Information settings
          setFlatBlock(numericToBoolean(settings.allowFlatBlockField?.value));
          setGuardianName(numericToBoolean(settings.allowGuardianName?.value));

          // Set Application Features settings
          setVoiceFeature(numericToBoolean(settings.allowVoiceFeature?.value));
          setWishlist(numericToBoolean(settings.allowWishlist?.value));
          setLocationTracking(numericToBoolean(settings.allowDriverTracking?.value));
          setIsProductScanning(numericToBoolean(settings.isProductScanning?.value));
          setDisplayDepartment(numericToBoolean(settings.displayDepartmentInCustomerApp?.value));

          // Set Operation Management settings
          setDeliveryScheduling(numericToBoolean(settings.allowDeliveryScheduling?.value));
          setDeliverySlot(numericToBoolean(settings.allowDeliverySlot?.value));
          setDeliverySlotType(settings.deliverySlotType?.addValue || "General");
          setScheduledDelivery(numericToBoolean(settings.allowScheduledDelivery?.value));
          setOrderCancellationWindow(Number(settings.orderCancellationWindow?.addValue) || 24);
          setCutOffTime(settings.cutOffTime?.value || "");
          setEstimatedDeliveryTime(Number(settings.estimatedDeliveryTime?.addValue) || 30);
          setApprovalRequired(numericToBoolean(settings.approvalRequired?.value));
          setDeliverySheetLimit(Number(settings.deliverySheetLimit?.addValue) || 0);
          
          // Set Delivery Zone settings
          const zoneType = settings.deliveryZoneType?.addValue || "radius";
          setDeliveryZoneType(zoneType);
          
          const zoneValue = settings.deliveryZoneValue?.addValue  || (zoneType === "radius" ? "10" : "");
          setDeliveryZoneValue(zoneValue);
          
          if (zoneType === "radius") {
            setDeliveryRadius(Number(zoneValue) || 10);
          }

          setHoSupplierCommission(numericToBoolean(settings.hoSupplierCommission?.value));
          setDeliveryCutoffTime(settings.deliveryCutoffTime?.addValue || "");
          setApprovalRequiredForOrder(numericToBoolean(settings.approvalRequiredForOrder?.value));

          // Set Purchase Options settings
          setPurchaseOptions(numericToBoolean(settings.allowPurchaseOption?.value));

          // Set Branch Operations settings
          setBranchStockAdjustment(numericToBoolean(settings.allowBranchStockAdjustment?.value));

          // Set Color settings
          setPrimaryColor(settings.primaryColourStatus?.addValue || "#8090FF");
          setSecondaryColor(settings.secondaryColourStatus?.addValue || "#108981");
          setTertiaryColor(settings.tertiaryColourStatus?.addValue || "#F59E8B");
        }
      } catch (error) {
        console.error("Error fetching native settings:", error);
      }
    };

    fetchNativeSettingList();
  }, [sessionList]);

  return (
    <>
      <Wrapper navUrl={navUrl} customStyle={{ height: "750px" }}>
        <WrapperSubHeader className="mainBreadcrumb">
          
        </WrapperSubHeader>
        <WrapperContent>
          <Row>
            <WrapperCard isCollapsableAnalytics={false}>
              <WrapperContent>
                <Row>
                  <div
                    style={{
                      background: "#f8fafc",
                      minHeight: "100vh",
                      padding: "24px",
                    }}
                  >
                    <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
                      <h1
                        style={{
                          fontSize: "24px",
                          fontWeight: "600",
                          marginBottom: "4px",
                        }}
                      >
                        Native App Settings
                      </h1>
                      <div
                        style={{
                          color: "#6b7280",
                          marginBottom: "24px",
                        }}
                      >
                        {navUrl}
                      </div>

                      {isLoading && (
                        <div style={{
                          position: "fixed",
                          top: "20px",
                          right: "20px",
                          background: "#4f46e5",
                          color: "white",
                          padding: "10px 20px",
                          borderRadius: "4px",
                          zIndex: 1000
                        }}>
                          Saving...
                        </div>
                      )}

                      {hasAnySetting(['allowDriverApp', 'allowDriverIncentive', 'perOrderCommission', 'perKmCharge', 'commissionOnFulfillment']) && (
                        <Card>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              marginBottom: "20px",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <FaTruck size={18} color="#4f46e5" />
                              <div>
                                <div style={{ fontSize: "16px", fontWeight: "600", marginBottom: "4px" }}>Driver Management</div>
                                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                                  Configure driver app and incentive settings.
                                </div>
                              </div>
                            </div>
                          </div>

                          <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "20px" }}>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "16px",
                                marginBottom: "20px",
                              }}
                            >
                              {hasSetting('allowDriverApp') && (
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <div>
                                    <div style={{ fontSize: "14px" }}>Driver App</div>
                                    <div style={{ fontSize: "12px", color: "#6b7280" }}>Enable mobile app for drivers</div>
                                  </div>
                                  <Switch checked={driverApp} onChange={handleDriverAppChange} />
                                </div>
                              )}

                              {hasSetting('allowDriverIncentive') && driverApp && (
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                  <div>
                                    <div style={{ fontSize: "14px" }}>Driver Incentive</div>
                                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                                      Enable commission and charges for drivers
                                    </div>
                                  </div>
                                  <Switch
                                    checked={driverIncentive}
                                    onChange={handleDriverIncentiveChange}
                                  />
                                </div>
                              )}
                            </div>

                            {(driverApp && driverIncentive) && (
                              <>
                                {/* Commission Type Radio Buttons */}
                                {hasSetting('perOrderCommissionType') && (
                                  <div style={{ marginBottom: "16px" }}>
                                    <div style={{ fontSize: "14px", fontWeight: "500", marginBottom: "12px" }}>
                                      Commission Type
                                    </div>
                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: "12px",
                                        maxWidth: "500px",
                                      }}
                                    >
                                      <RadioButton
                                        label="Amount"
                                        description="Fixed amount per order"
                                        value="amount"
                                        checked={orderCommissionType === "amount"}
                                        onChange={handleOrderCommissionTypeChange}
                                      />
                                      <RadioButton
                                        label="Percentage"
                                        description="Percentage of order value"
                                        value="percentage"
                                        checked={orderCommissionType === "percentage"}
                                        onChange={handleOrderCommissionTypeChange}
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* Commission Input Field */}
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "16px",
                                    marginBottom: "16px",
                                  }}
                                >
                                  <InputField
                                    label={`Per Order Commission ${orderCommissionType === "percentage" ? "(%)" : "(₹)"}`}
                                    placeholder={`Enter ${orderCommissionType === "percentage" ? "percentage" : "amount"}`}
                                    value={orderCommission}
                                    onChange={handleOrderCommissionChange}
                                    type="number"
                                    unit={orderCommissionType === "percentage" ? "%" : "₹"}
                                    show={hasSetting('perOrderCommission')}
                                  />
                                  <InputField
                                    label="Per KM Charge (₹)"
                                    placeholder="Enter amount"
                                    value={kmCharge}
                                    onChange={handleKmChargeChange}
                                    type="number"
                                    unit="₹"
                                    show={hasSetting('perKmCharge')}
                                  />
                                </div>
                              </>
                            )}

                            {hasSetting('commissionOnFulfillment') && driverIncentive && (
                              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                <div style={{ fontSize: "14px" }}>Commission on Fulfillment Only</div>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                                    Commission paid only for delivered orders
                                  </div>
                                  <Switch checked={commissionOnFulfillment} onChange={handleCommissionOnFulfillmentChange} />
                                </div>
                              </div>
                            )}
                          </div>
                        </Card>
                      )}

                      {hasAnySetting(['allowFlatBlockField', 'allowGuardianName']) && (
                        <Card>
                          <div style={{ marginBottom: "20px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <FaPhoneAlt size={18} color="#4f46e5" />
                              <div>
                                <div style={{ fontSize: "16px", fontWeight: "600", marginBottom: "4px" }}>Contact Information</div>
                                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                                  Manage customer contact field preferences
                                </div>
                              </div>
                            </div>
                          </div>

                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "16px",
                            }}
                          >
                            <FeatureBox
                              title="Flat/Block/Floor Field"
                              description="Enable flat/block number in contact forms"
                              checked={flatBlock}
                              onChange={handleFlatBlockChange}
                              show={hasSetting('allowFlatBlockField')}
                            />
                            <FeatureBox
                              title="Guardian Name Field"
                              description="Require guardian name in contact information"
                              checked={guardianName}
                              onChange={handleGuardianNameChange}
                              show={hasSetting('allowGuardianName')}
                            />
                          </div>
                        </Card>
                      )}

                      {hasAnySetting(['allowVoiceFeature', 'allowWishlist', 'allowDriverTracking', 'isProductScanning', 'displayDepartmentInCustomerApp']) && (
                        <Card>
                          <div style={{ marginBottom: "20px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <FaCogs size={18} color="#4f46e5" />
                              <div>
                                <div style={{ fontSize: "16px", fontWeight: "600", marginBottom: "4px" }}>Application Features</div>
                                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                                  Configure advanced features for customer and driver applications
                                </div>
                              </div>
                            </div>
                          </div>

                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr 1fr",
                              gap: "16px",
                            }}
                          >
                            <FeatureBox
                              title="Voice Feature"
                              description="Enable voice commands and search"
                              checked={voiceFeature}
                              onChange={handleVoiceFeatureChange}
                              icon={<FaMicrophone color="#4f46e5" />}
                              show={hasSetting('allowVoiceFeature')}
                            />
                            <FeatureBox
                              title="Wishlist"
                              description="Allow customers to save favorite items"
                              checked={wishlist}
                              onChange={handleWishlistChange}
                              icon={<FaHeart color="#4f46e5" />}
                              show={hasSetting('allowWishlist')}
                            />
                            <FeatureBox
                              title="Driver Location Tracking"
                              description="Real-time driver location monitoring"
                              checked={locationTracking}
                              onChange={handleLocationTrackingChange}
                              icon={<FaMapMarkerAlt color="#4f46e5" />}
                              show={hasSetting('allowDriverTracking')}
                            />
                            
                            <FeatureBox
                              title="Product Scanning"
                              description="Enable barcode/QR code scanning for products"
                              checked={isProductScanning}
                              onChange={handleProductScanningChange}
                              icon={<FaBarcode color="#4f46e5" />}
                              show={hasSetting('isProductScanning')}
                            />
                            
                            <FeatureBox
                              title="Display Department"
                              description="Show department categories in customer app"
                              checked={displayDepartment}
                              onChange={handleDisplayDepartmentChange}
                              icon={<FaSitemap color="#4f46e5" />}
                              show={hasSetting('displayDepartmentInCustomerApp')}
                            />
                          </div>
                        </Card>
                      )}

                      {hasAnySetting([
                        'allowDeliveryScheduling', 'allowDeliverySlot', 'deliverySlotType', 'allowScheduledDelivery',
                        'orderCancellationWindow', 'cutOffTime', 'estimatedDeliveryTime',
                        'approvalRequired', 'deliverySheetLimit', 'deliveryZoneType',
                        'deliveryZoneValue', 'hoSupplierCommission', 'deliveryCutoffTime',
                        'approvalRequiredForOrder'
                      ]) && (
                        <Card>
                          <div style={{ marginBottom: "20px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <FaWarehouse size={18} color="#4f46e5" />
                              <div>
                                <div style={{ fontSize: "16px", fontWeight: "600", marginBottom: "4px" }}>Operation Management</div>
                                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                                  Configure delivery, order, and commission settings
                                </div>
                              </div>
                            </div>
                          </div>

                          <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "20px" }}>
                            {hasAnySetting(['allowDeliveryScheduling', 'allowDeliverySlot', 'deliverySlotType', 'allowScheduledDelivery']) && (
                              <div style={{ marginBottom: "24px" }}>
                                <div style={{ fontSize: "14px", fontWeight: "600", marginBottom: "16px" }}>Delivery Scheduling</div>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr 1fr",
                                    gap: "16px",
                                    marginBottom: "16px",
                                  }}
                                >
                                  <FeatureBox
                                    title="Delivery Scheduling"
                                    description="Enable delivery scheduling system"
                                    checked={deliveryScheduling}
                                    onChange={handleDeliverySchedulingChange}
                                    icon={<FaCalendarAlt color="#4f46e5" />}
                                    show={hasSetting('allowDeliveryScheduling')}
                                  />
                                  <FeatureBox
                                    title="Delivery Slot"
                                    description="Allow customers to choose delivery slots"
                                    checked={deliverySlot}
                                    onChange={handleDeliverySlotChange}
                                    icon={<FaClock color="#4f46e5" />}
                                    show={hasSetting('allowDeliverySlot')}
                                  />
                                  <FeatureBox
                                    title="Scheduled Delivery"
                                    description="Enable scheduled delivery options"
                                    checked={scheduledDelivery}
                                    onChange={handleScheduledDeliveryChange}
                                    icon={<FaTruck color="#4f46e5" />}
                                    show={hasSetting('allowScheduledDelivery')}
                                  />
                                </div>

                                {deliverySlot && hasSetting('deliverySlotType') && (
                                  <div style={{ marginTop: "16px" }}>
                                    <div style={{ fontSize: "14px", fontWeight: "500", marginBottom: "12px" }}>
                                      Delivery Slot Type
                                    </div>
                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 1fr",
                                        gap: "12px",
                                        maxWidth: "500px",
                                      }}
                                    >
                                      <RadioButton
                                        label="General Slots"
                                        description="Pre-defined time slots (e.g., Morning, Afternoon, Evening)"
                                        value="General"
                                        checked={deliverySlotType === "General"}
                                        onChange={handleDeliverySlotTypeChange}
                                      />
                                      <RadioButton
                                        label="Custom Slots"
                                        description="Customizable time slots with specific start and end times"
                                        value="Custom"
                                        checked={deliverySlotType === "Custom"}
                                        onChange={handleDeliverySlotTypeChange}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {hasAnySetting(['orderCancellationWindow', 'cutOffTime', 'estimatedDeliveryTime', 'deliveryCutoffTime']) && (
                              <div style={{ marginBottom: "24px" }}>
                                <div style={{ fontSize: "14px", fontWeight: "600", marginBottom: "16px" }}>Time Management</div>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                    gap: "16px",
                                    marginBottom: "16px",
                                  }}
                                >
                                  <InputField
                                    label="Order Cancellation Window (Hours)"
                                    placeholder="e.g., 24"
                                    value={orderCancellationWindow}
                                    onChange={handleOrderCancellationWindowChange}
                                    type="number"
                                    show={hasSetting('orderCancellationWindow')}
                                  />
                                  <InputField
                                    label="Cut-off Time"
                                    placeholder="HH:MM"
                                    value={cutOffTime}
                                    onChange={handleCutOffTimeChange}
                                    type="time"
                                    show={hasSetting('cutOffTime')}
                                  />
                                  <InputField
                                    label="Estimated Delivery Time (Minutes)"
                                    placeholder="e.g., 30"
                                    value={estimatedDeliveryTime}
                                    onChange={handleEstimatedDeliveryTimeChange}
                                    type="number"
                                    show={hasSetting('estimatedDeliveryTime')}
                                  />
                                  <InputField
                                    label="Delivery Cutoff Time"
                                    placeholder="HH:MM"
                                    value={deliveryCutoffTime}
                                    onChange={handleDeliveryCutoffTimeChange}
                                    type="time"
                                    show={hasSetting('deliveryCutoffTime')}
                                  />
                                </div>
                              </div>
                            )}

                            {hasAnySetting(['approvalRequired', 'deliverySheetLimit', 'approvalRequiredForOrder']) && (
                              <div style={{ marginBottom: "24px" }}>
                                <div style={{ fontSize: "14px", fontWeight: "600", marginBottom: "16px" }}>Order Management</div>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "16px",
                                    marginBottom: "16px",
                                  }}
                                >
                                  {hasSetting('approvalRequired') && (
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                      <div>
                                        <div style={{ fontSize: "14px" }}>Approval Required</div>
                                        <div style={{ fontSize: "12px", color: "#6b7280" }}>
                                          Orders require manual approval before processing
                                        </div>
                                      </div>
                                      <Switch checked={approvalRequired} onChange={handleApprovalRequiredChange} />
                                    </div>
                                  )}
                                  
                                  {hasSetting('approvalRequiredForOrder') && (
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                      <div>
                                        <div style={{ fontSize: "14px" }}>Approval Required For Order</div>
                                        <div style={{ fontSize: "12px", color: "#6b7280" }}>
                                          Orders require manual approval before processing
                                        </div>
                                      </div>
                                      <Switch checked={approvalRequiredForOrder} onChange={handleApprovalRequiredForOrderChange} />
                                    </div>
                                  )}

                                  <InputField
                                    label="Delivery Sheet Order Limit"
                                    placeholder="Enter limit"
                                    value={deliverySheetLimit}
                                    onChange={handleDeliverySheetLimitChange}
                                    type="number"
                                    show={hasSetting('deliverySheetLimit')}
                                  />
                                </div>
                              </div>
                            )}
                            
                            {hasAnySetting(['deliveryZoneType', 'deliveryZoneValue']) && (
                              <div style={{ marginBottom: "24px" }}>
                                <div style={{ fontSize: "14px", fontWeight: "600", marginBottom: "16px" }}>Delivery Zone Configuration</div>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "16px",
                                    marginBottom: "16px",
                                  }}
                                >
                                  {hasSetting('deliveryZoneType') && (
                                    <div>
                                      <div style={{ fontSize: "14px", marginBottom: "8px" }}>Delivery Zone Type</div>
                                      <select
                                        value={deliveryZoneType}
                                        onChange={(e) => handleDeliveryZoneTypeChange(e.target.value)}
                                        style={{
                                          width: "100%",
                                          padding: "8px 12px",
                                          border: "1px solid #e5e7eb",
                                          borderRadius: "6px",
                                          background: "#fff",
                                          color: "#1c1c1cff",
                                        }}
                                      >
                                        <option value="radius">Radius</option>
                                        <option value="pincode">Pincode</option>
                                      </select>
                                    </div>
                                  )}
                                  
                                  {hasSetting('deliveryZoneValue') && (
                                    <div>
                                      {deliveryZoneType === "radius" ? (
                                        <InputField
                                          label="Delivery Radius (KM)"
                                          placeholder="e.g., 10"
                                          value={deliveryRadius}
                                          onChange={handleDeliveryRadiusChange}
                                          type="number"
                                          unit="KM"
                                          show={true}
                                        />
                                      ) : (
                                        <div>
                                          <div style={{ fontSize: "14px", marginBottom: "8px" }}>Delivery Pincodes</div>
                                          <input
                                            type="text"
                                            value={deliveryZoneValue}
                                            onChange={(e) => handlePincodesChange(e.target.value)}
                                            placeholder="e.g., 363020,363021,363022"
                                            style={{
                                              width: "100%",
                                              padding: "8px 12px",
                                              border: "1px solid #e5e7eb",
                                              borderRadius: "6px",
                                              background: "#fff",
                                              color: "#1c1c1cff",
                                            }}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {hasSetting('hoSupplierCommission') && (
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <div>
                                  <div style={{ fontSize: "14px" }}>HO Supplier Commission</div>
                                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                                    Enable head office supplier commission tracking
                                  </div>
                                </div>
                                <Switch checked={hoSupplierCommission} onChange={handleHoSupplierCommissionChange} />
                              </div>
                            )}
                          </div>
                        </Card>
                      )}

                      {hasSetting('allowPurchaseOption') && (
                        <Card>
                          <div style={{ marginBottom: "20px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <FaShoppingBag size={18} color="#4f46e5" />
                              <div>
                                <div style={{ fontSize: "16px", fontWeight: "600", marginBottom: "4px" }}>Purchase Options</div>
                                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                                  Configure purchase and buying preferences
                                </div>
                              </div>
                            </div>
                          </div>

                          <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "20px" }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "16px",
                                border: "1px solid #e5e7eb",
                                borderRadius: "8px",
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <FaShoppingCart color="#4f46e5" />
                                <div>
                                  <div style={{ fontSize: "14px", fontWeight: "500" }}>Enable Purchase Options</div>
                                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                                    Allow customers to configure purchase preferences and options
                                  </div>
                                </div>
                              </div>
                              <Switch checked={purchaseOptions} onChange={handlePurchaseOptionsChange} />
                            </div>
                          </div>
                        </Card>
                      )}

                      {hasSetting('allowBranchStockAdjustment') && (
                        <Card>
                          <div style={{ marginBottom: "20px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <FaStore size={18} color="#4f46e5" />
                              <div>
                                <div style={{ fontSize: "16px", fontWeight: "600", marginBottom: "4px" }}>Branch Operations</div>
                                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                                  Manage branch-level operational settings
                                </div>
                              </div>
                            </div>
                          </div>

                          <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "20px" }}>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr",
                                gap: "16px",
                              }}
                            >
                              <FeatureBox
                                title="Allow Branch Stock Adjustment"
                                description="Enable branch managers to adjust stock levels and inventory"
                                checked={branchStockAdjustment}
                                onChange={handleBranchStockAdjustmentChange}
                                icon={<FaWarehouse color="#4f46e5" />}
                                show={hasSetting('allowBranchStockAdjustment')}
                              />
                            </div>
                          </div>
                        </Card>
                      )}

                      {hasAnySetting(['primaryColourStatus', 'secondaryColourStatus', 'tertiaryColourStatus']) && (
                        <Card>
                          <div style={{ marginBottom: "20px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <FaPalette size={18} color="#4f46e5" />
                              <div>
                                <div style={{ fontSize: "16px", fontWeight: "600", marginBottom: "4px" }}>App Theme</div>
                                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                                  Customize your app theme colors
                                </div>
                              </div>
                            </div>
                          </div>

                          <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "20px" }}>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr 1fr",
                                gap: "115px",
                                padding: "0 20px",
                              }}
                            >
                              <ColorPicker 
                                value={primaryColor}
                                onChange={(color) => setPrimaryColor(color)}
                                onSave={handleColorChange}
                                colorType="primary"
                                className="primary"
                                isLoading={isLoading}
                              />

                              <ColorPicker 
                                value={secondaryColor}
                                onChange={(color) => setSecondaryColor(color)}
                                onSave={handleColorChange}
                                colorType="secondary"
                                className="secondary"
                                isLoading={isLoading}
                              />

                              <ColorPicker 
                                value={tertiaryColor}
                                onChange={(color) => setTertiaryColor(color)}
                                onSave={handleColorChange}
                                colorType="tertiary"
                                className="tertiary"
                                isLoading={isLoading}
                              />
                            </div>
                          </div>
                        </Card>
                      )}

                      {Object.keys(settingsMap).length === 0 && (
                        <Card>
                          <div style={{ textAlign: "center", padding: "40px" }}>
                            <FaCogs size={48} color="#9ca3af" style={{ marginBottom: "16px" }} />
                            <div style={{ fontSize: "18px", fontWeight: "500", color: "#6b7280", marginBottom: "8px" }}>
                              No Settings Available
                            </div>
                            <div style={{ fontSize: "14px", color: "#9ca3af" }}>
                              There are no native app settings configured for your account.
                            </div>
                          </div>
                        </Card>
                      )}
                    </div>
                  </div>
                </Row>
              </WrapperContent>
            </WrapperCard>
          </Row>
        </WrapperContent>
      </Wrapper>
    </>
  );
};

export default NativeSetting;